
import { getFirestore, collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, arrayUnion, setDoc, increment, arrayRemove, deleteDoc, deleteField, orderBy, limit, } from 'firebase/firestore';
import { getFirestoreInstance } from './firebaseHelper';

// Cache keys
const EXPERIENCE_YEARS_CACHE_KEY = 'experience_years_cache_';
const CURRENT_YEAR_CACHE_KEY = 'current_year_cache';
const YEAR_VERIFICATION_TIMESTAMP_KEY = 'year_verification_timestamp'; // New key for tracking verification

/**
 * Get cached experience years for a user
 * @param {string} userId - The user ID
 * @returns {Object|null} - The cached experience years or null if not cached
 */
export const getCachedExperienceYears = (userId) => {
    if (!userId) return null;
    
    const cacheKey = `${EXPERIENCE_YEARS_CACHE_KEY}${userId}`;
    const cachedValue = localStorage.getItem(cacheKey);
    
    if (cachedValue) {
      try {
        const parsed = JSON.parse(cachedValue);
        return {
          overallExperienceYears: parsed.overallExperienceYears,
          professionalExperienceYears: parsed.professionalExperienceYears,
          startedLearningYear: parsed.startedLearningYear,
          startedProfessionalYear: parsed.startedProfessionalYear,
          cachedAt: new Date(parsed.cachedAt),
          calculatedWithYear: parsed.calculatedWithYear
        };
      } catch (error) {
        console.error('Error parsing cached experience years:', error);
        localStorage.removeItem(cacheKey);
        return null;
      }
    }
    
    return null;
  };
  
  /**
   * Save experience years to cache
   * @param {string} userId - The user ID
   * @param {Object} data - The experience years data to cache
   */
  export const cacheExperienceYears = (userId, data) => {
    if (!userId) return;
    
    const cacheKey = `${EXPERIENCE_YEARS_CACHE_KEY}${userId}`;
    const cacheData = {
      ...data,
      cachedAt: new Date().toISOString()
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  };
  
  /**
   * Get cached current year
   * @returns {Object|null} - The cached current year data or null if not cached
   */
  export const getCachedCurrentYear = () => {
    const cachedValue = localStorage.getItem(CURRENT_YEAR_CACHE_KEY);
    
    if (cachedValue) {
      try {
        return JSON.parse(cachedValue);
      } catch (error) {
        console.error('Error parsing cached current year:', error);
        localStorage.removeItem(CURRENT_YEAR_CACHE_KEY);
      }
    }
    
    return null;
  };
  
  /**
   * Get last verification timestamp
   * @returns {Date|null} - The last verification timestamp or null if not found
   */
  export const getLastVerificationTimestamp = () => {
    const timestampValue = localStorage.getItem(YEAR_VERIFICATION_TIMESTAMP_KEY);
    
    if (timestampValue) {
      try {
        return new Date(JSON.parse(timestampValue));
      } catch (error) {
        console.error('Error parsing verification timestamp:', error);
        localStorage.removeItem(YEAR_VERIFICATION_TIMESTAMP_KEY);
      }
    }
    
    return null;
  };
  
  /**
   * Set last verification timestamp
   */
  export const setLastVerificationTimestamp = () => {
    localStorage.setItem(
      YEAR_VERIFICATION_TIMESTAMP_KEY, 
      JSON.stringify(new Date().toISOString())
    );
  };
  
  /**
   * Cache current year
   * @param {number} year - The current year to cache
   * @param {boolean} verified - Whether this year has been verified with an API
   */
  export const cacheCurrentYear = (year, verified = false) => {
    if (!year) return;
    
    localStorage.setItem(CURRENT_YEAR_CACHE_KEY, JSON.stringify({
      year,
      cachedAt: new Date().toISOString(),
      verified
    }));
    
    // If verified via API, also update the verification timestamp
    if (verified) {
      setLastVerificationTimestamp();
    }
  };
  
  /**
   * Get current year from server API
   * @returns {Promise<number>} - The current year
   */
  export const getCurrentYearFromServer = async () => {
    try {
      console.log('Fetching current year from server API');
      const response = await fetch('/api/getServerTime', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get server time');
      }
      
      const data = await response.json();
      const serverTime = new Date(data.now);
      const year = serverTime.getFullYear();
      
      console.log('Server returned year:', year);
      
      // Cache the year with verified flag
      cacheCurrentYear(year, true);
      
      return year;
    } catch (error) {
      console.error('Error getting current year from server:', error);
      throw error;
    }
  };
  
  /**
   * Check if year verification is needed based on local date and cached data
   * @returns {boolean} - Whether verification is needed
   */
  export const isYearVerificationNeeded = () => {
    const localYear = new Date().getFullYear();
    const cachedYearData = getCachedCurrentYear();
    
    // If no cached year, verification is needed
    if (!cachedYearData) return true;
    
    // If local year differs from cached year, verification might be needed
    if (localYear !== cachedYearData.year) {
      // Check if we've already verified today (to prevent repeated API calls)
      const lastVerification = getLastVerificationTimestamp();
      if (!lastVerification) return true;
      
      // If last verification was more than 24 hours ago, verify again
      const now = new Date();
      const hoursSinceLastVerification = (now - lastVerification) / (1000 * 60 * 60);
      
      return hoursSinceLastVerification > 24;
    }
    
    // If cached year was already verified via API, no need to verify again
    if (cachedYearData.verified) return false;
    
    // Otherwise, verify unverified years once per session
    return !cachedYearData.verified;
  };
  
  /**
   * Get current year with smart caching
   * @returns {Promise<number>} - The current year
   */
  export const getCurrentYear = async () => {
    const cachedYearData = getCachedCurrentYear();
    const localYear = new Date().getFullYear();
    
    // If we have cached data and verification is not needed, use cached year
    if (cachedYearData && !isYearVerificationNeeded()) {
      console.log('Using cached year:', cachedYearData.year);
      return cachedYearData.year;
    }
    
    console.log('Verification needed, checking server year');
    
    // Verification is needed, try server API
    try {
      return await getCurrentYearFromServer();
    } catch (serverError) {
      console.error('Server API failed:', serverError);
      
      // As a last resort, use local year but mark as unverified
      console.log('Using local year as fallback:', localYear);
      cacheCurrentYear(localYear, false);
      return localYear;
    }
  };
  
  /**
   * Calculate experience years based on start years and current year
   * @param {Object} data - Object containing start years
   * @param {number} currentYear - The current year
   * @returns {Object} - The calculated experience years
   */
  export const calculateExperienceYears = (data, currentYear) => {
    const { startedLearningYear, startedProfessionalYear } = data;
    
    let overallExperienceYears = null;
    let professionalExperienceYears = null;
    
    if (startedLearningYear && !isNaN(parseInt(startedLearningYear))) {
      overallExperienceYears = currentYear - parseInt(startedLearningYear);
      if (overallExperienceYears < 0) overallExperienceYears = 0;
    }
    
    if (startedProfessionalYear && !isNaN(parseInt(startedProfessionalYear))) {
      professionalExperienceYears = currentYear - parseInt(startedProfessionalYear);
      if (professionalExperienceYears < 0) professionalExperienceYears = 0;
    }
    
    return {
      overallExperienceYears,
      professionalExperienceYears,
      startedLearningYear,
      startedProfessionalYear,
      calculatedWithYear: currentYear
    };
  };
  
  /**
   * Check if experience years need to be updated
   * @param {Object} cachedData - The cached experience years data
   * @param {Object} profileData - The user's profile data
   * @param {number} currentYear - The current year
   * @returns {boolean} - Whether the experience years need to be updated
   */
  export const shouldUpdateExperienceYears = (cachedData, profileData, currentYear) => {
    if (!cachedData || !profileData) return false;
    
    // If calculation year has changed, update is needed
    if (cachedData.calculatedWithYear !== currentYear) {
      return true;
    }
    
    // If start years have changed, update is needed
    if (
      cachedData.startedLearningYear !== profileData.startedLearningYear ||
      cachedData.startedProfessionalYear !== profileData.startedProfessionalYear
    ) {
      return true;
    }
    
    // If stored experience years don't match calculated values, update is needed
    const calculated = calculateExperienceYears(profileData, currentYear);
    
    return (
      calculated.overallExperienceYears !== profileData.overallExperienceYears ||
      calculated.professionalExperienceYears !== profileData.professionalExperienceYears
    );
  };
  
  /**
   * Update experience years in Firestore
   * @param {string} userId - The user ID
   * @param {Object} experienceData - The experience years data to update
   * @returns {Promise<boolean>} - Whether the update was successful
   */
  export const updateExperienceYearsInFirestore = async (userId, experienceData) => {
    if (!userId) return false;
    
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userId);
      
      await updateDoc(userRef, {
        overallExperienceYears: experienceData.overallExperienceYears,
        professionalExperienceYears: experienceData.professionalExperienceYears
      });
      
      console.log(`Updated experience years in Firestore for user ${userId}`);
      return true;
    } catch (error) {
      console.error('Error updating experience years in Firestore:', error);
      return false;
    }
  };